import omit from 'lodash/omit';
import { QUESTION_IDS_BY_DISEASE } from 'modules/questionnaire/constants';

export const HAS_SSN_FIELD = 'hasSsn';
export const HAS_TREATMENT_FIELD = 'hasTreatment';
export const HELP_RECEIVED_BEFORE_FIELD = 'helpReceivedBefore';
export const INCOME_FIELD = 'income';
export const INSURANCE_FIELD = 'insurance';
export const NUM_OF_HOUSEHOLD_MEMBERS_FIELD = 'numOfHouseholdMembers';

export const FINANCE_FIELDS = [
  HAS_SSN_FIELD,
  HAS_TREATMENT_FIELD,
  HELP_RECEIVED_BEFORE_FIELD,
  INCOME_FIELD,
  INSURANCE_FIELD,
  NUM_OF_HOUSEHOLD_MEMBERS_FIELD,
];

const EMPTY_FINANCE_PROFILE_FIELDS = Object.fromEntries(FINANCE_FIELDS.map((key) => [key, null]));

const adjustFinanceParamsValue = (value) => ({
  yes: true,
  no: false,
}[value] ?? value);

const adjustGender = (value) => {
  const gender = Array.isArray(value) ? value[0] : value;
  // for brain raw gender can be 'NS' (meaning not specified); it should be replaced with null
  return ['male', 'female', 'other', null].includes(gender) ? gender : null;
};

export const getFinanceParams = (user, finance_profile = {}, aidTypes = [])=>{
  const userWixId = user?.info?.user_wix_id;
  const email = user?.info?.email;
  const dob = user?.personal?.dob;
  const gender = user?.personal?.gender;
  const indication = user?.personal?.condition;
  let race = user?.condition_profile.race;
  const stageField = QUESTION_IDS_BY_DISEASE[indication]?.patientStage;
  let stage = stageField ? user?.condition_profile[stageField] : null;
  // handle not_sure case
  if (stage && Number.isNaN(Number.parseFloat(stage))) {
    stage = null;
  }

  let age;
  try {
    age = new Date().getFullYear() - new Date(dob).getFullYear();
  } catch (e) {
    age = null;
  }

  const financeProfile = Object.fromEntries(
    Object.keys(EMPTY_FINANCE_PROFILE_FIELDS).map((key) => {
      const value = finance_profile[key] ?? EMPTY_FINANCE_PROFILE_FIELDS[key];
      return [
        key,
        EMPTY_FINANCE_PROFILE_FIELDS[key] === value ? value : adjustFinanceParamsValue(value),
      ];
    }),
  );

  let location = null;
  if (Object.keys(user?.info?.country || {}).length) {
    location = omit(user?.info?.country?.location, ['approximateLat', 'approximateLng']);
  }

  return {
    ...financeProfile,
    userWixId,
    email,
    aidTypes,
    age,
    gender: adjustGender(gender),
    stage: Array.isArray(stage) ? stage[0] : stage,
    indication,
    race: race || null,
    location,
  };
};

export const prepareFinanceProfileToUpsert = (financeProfile) => (
  omit(financeProfile, ['userWixId', 'email', 'location'])
);

export const preparePatientFinanceProfileToUpsert = (financeProfile) => (
  omit(financeProfile, ['email', 'location'])
);

export const prepareParamsToGetPatientMatchedPrograms = (financeProfile) => (
  omit(financeProfile, ['userWixId', 'email'])
);

export const isProgramEligible = (program) => {
  if (!program.validatedFields) return true;
  const fields = Object.keys(program.validatedFields);
  if (!fields.length) return true;
  return fields.every((field) => program.validatedFields[field]);
};

export const getEmptyFinanceProfile = () => ({ ...EMPTY_FINANCE_PROFILE_FIELDS });

export const isFinanceProfileEmpty = (financeProfile) => !financeProfile || Object.entries(EMPTY_FINANCE_PROFILE_FIELDS)
  .every(([key, defaultValue]) => financeProfile[key] === defaultValue);
